/**
 * Confirm modal for activating (publishing / restoring) a specific
 * version from the versions rail. The activate endpoint doesn't
 * require a commit message — the audit row already captures
 * before/after activeVersion + actor — but the operator should
 * still see WHICH version they're flipping to before it goes live.
 */
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import type { AdminPromptVersion } from '@/lib/api'

export interface ActivateConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: AdminPromptVersion | null
  /** What's currently active — included so the confirmation copy
   * can say "v7 → v3" not just "→ v3". */
  currentVersion: number | null
  onConfirm: () => Promise<void>
  activating?: boolean
}

export function ActivateConfirmDialog({
  open,
  onOpenChange,
  target,
  currentVersion,
  onConfirm,
  activating = false,
}: ActivateConfirmDialogProps) {
  if (!target) return null
  return (
    <Dialog open={open} onOpenChange={(o) => !activating && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Activate v{target.version}?</DialogTitle>
          <DialogDescription>
            {currentVersion === null
              ? `v${target.version} will become the active version.`
              : `Flips the active pointer from v${currentVersion} to v${target.version}. The current version stays in history and can be reactivated later.`}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <div className="font-mono text-xs text-muted-foreground">v{target.version}</div>
            <div className="mt-1">{target.commitMessage}</div>
          </div>
          <p className="text-xs text-muted-foreground">
            The next time an agent fetches this prompt, it will receive this version's body. Cache busts atomically on publish; propagation is sub-second.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={activating}>Cancel</Button>
          <Button onClick={() => void onConfirm()} disabled={activating}>
            {activating ? 'Activating…' : `Activate v${target.version}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
